import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ReadingsChart from '../components/ReadingsChart';
import { authService } from '../services/authService';
import { gatewayService } from '../services/gatewayService';
import { sensorService } from '../services/sensorService';
import { Gateway, Sensor, SensorReading, User } from '../types';
import { convertTemperature, getStoredTempUnit, tempUnitLabel } from '../utils/temperatureUtils';

type Metric = {
  key: keyof SensorReading;
  label: string;
  format: (value: number) => string;
};

const GatewayDetail: React.FC = () => {
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [gateway, setGateway] = useState<Gateway | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [latestReadings, setLatestReadings] = useState<Record<number, SensorReading>>({});
  const [selectedSensorId, setSelectedSensorId] = useState<number | null>(null);
  const [sensorSearch, setSensorSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async (refresh = false) => {
    if (!gatewayId) return;
    try {
      refresh ? setRefreshing(true) : setLoading(true);
      setError('');
      const decodedGatewayId = decodeURIComponent(gatewayId);
      const [currentUser, matchedGateway, allSensors] = await Promise.all([
        authService.getCurrentUser(),
        gatewayService.getGateway(decodedGatewayId),
        sensorService.getSensors(),
      ]);
      setUser(currentUser);

      const registeredIds = new Set(matchedGateway.sensors.map((sensor) => sensor.id));
      const registeredSensors = allSensors.filter((sensor) => registeredIds.has(sensor.id));
      const readings = await sensorService.getLatestReadingsBulk(registeredSensors.map((sensor) => sensor.id));

      setGateway(matchedGateway);
      setSensors(registeredSensors);
      setLatestReadings(Object.fromEntries(readings.map((reading) => [reading.sensor_id, reading])));
      setSelectedSensorId((current) =>
        current && registeredIds.has(current) ? current : registeredSensors[0]?.id ?? null,
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('gateways.detail.loadError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [gatewayId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSensorSearch('');
  }, [gatewayId]);

  const selectedSensor = sensors.find((sensor) => sensor.id === selectedSensorId) ?? null;
  const filteredSensors = useMemo(() => {
    const query = sensorSearch.trim().toLowerCase();
    if (!query) return sensors;

    return sensors.filter((sensor) =>
      sensor.name.toLowerCase().includes(query)
      || sensor.device_id.toLowerCase().includes(query)
      || sensor.sensor_type.toLowerCase().includes(query)
      || (sensor.grower_name ?? '').toLowerCase().includes(query),
    );
  }, [sensorSearch, sensors]);
  const latestReading = selectedSensorId ? latestReadings[selectedSensorId] ?? null : null;
  const temperatureUnit = getStoredTempUnit();

  const metrics = useMemo<Metric[]>(() => {
    const temperature = (value: number) =>
      `${(convertTemperature(value, temperatureUnit) ?? value).toFixed(1)}${tempUnitLabel(temperatureUnit)}`;
    const decimal = (suffix: string) => (value: number) => `${value.toFixed(1)}${suffix}`;
    const conductivity = (value: number) => `${(value / 1000).toFixed(2)} mS/cm`;

    return [
      { key: 'temperature', label: t('dashboard.temperature'), format: temperature },
      { key: 'soil_moisture', label: t('dashboard.moisture'), format: decimal('%') },
      { key: 'electrical_conductivity', label: t('common.ec'), format: conductivity },
      { key: 'battery_voltage', label: t('dashboard.battery'), format: (value) => `${value.toFixed(2)} V` },
      { key: 'temp_ds18b20', label: 'DS18B20', format: temperature },
      { key: 'leaf_temperature', label: t('dashboard.leafTemperature'), format: temperature },
      { key: 'leaf_moisture', label: t('dashboard.leafMoisture'), format: decimal('%') },
      { key: 'air_temperature', label: t('lht65n.airTemperature'), format: temperature },
      { key: 'air_humidity', label: t('lht65n.humidity'), format: decimal('%') },
      { key: 'distance_mm', label: t('sensorDetail.distance'), format: (value) => `${value.toFixed(0)} mm` },
      { key: 'ph_value', label: t('wqsPh.ph'), format: (value) => value.toFixed(2) },
      { key: 'ph_temperature', label: t('wqsPh.probeTemperature'), format: temperature },
      { key: 'ec_temperature', label: t('wqsEc.probeTemperature'), format: temperature },
      { key: 'par', label: t('par.par'), format: (value) => `${value.toFixed(0)} µmol/m²/s` },
      ...[1, 2, 3, 4].flatMap((probe) => [
        { key: `temp_soil${probe}` as keyof SensorReading, label: `${t('sensorDetail.probe')} ${probe} ${t('dashboard.temp')}`, format: temperature },
        { key: `water_soil${probe}` as keyof SensorReading, label: `${t('sensorDetail.probe')} ${probe} ${t('dashboard.moisture')}`, format: decimal('%') },
        { key: `conduct_soil${probe}` as keyof SensorReading, label: `${t('sensorDetail.probe')} ${probe} ${t('common.ec')}`, format: conductivity },
      ]),
    ];
  }, [t, temperatureUnit]);

  const visibleMetrics = latestReading
    ? metrics.filter((metric) => typeof latestReading[metric.key] === 'number')
    : [];

  const handleNewReading = useCallback((reading: SensorReading) => {
    setLatestReadings((current) => ({ ...current, [reading.sensor_id]: reading }));
  }, []);

  if (loading) {
    return (
      <Layout user={user ?? undefined}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user ?? undefined}>
      <div className="space-y-6">
        <div>
          <Link to={user?.is_admin ? '/gateways' : '/sensors'} className="text-sm text-primary-600 hover:text-primary-800">
            {user?.is_admin ? t('gateways.detail.back') : t('sensorDetail.backToSensors')}
          </Link>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 break-words">
                {gateway?.internal_name || gateway?.name || gateway?.gateway_id || t('gateways.title')}
              </h1>
              {gateway && (
                <p className="mt-1 text-sm text-gray-500 font-mono break-all">{gateway.gateway_id}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 rounded-md"
            >
              {refreshing ? t('gateways.detail.refreshing') : t('gateways.detail.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
        )}

        {gateway && (
          <div className="border-y border-gray-200 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">{t('gateways.colEUI')}</span><p className="mt-1 font-mono text-gray-900 break-all">{gateway.eui || '-'}</p></div>
            <div><span className="text-gray-500">{t('gateways.colInternalName')}</span><p className="mt-1 text-gray-900">{gateway.internal_name || t('gateways.noInternalName')}</p></div>
            <div><span className="text-gray-500">{t('gateways.colLocation')}</span><p className="mt-1 text-gray-900">{gateway.location || t('gateways.noLocation')}</p></div>
            <div><span className="text-gray-500">{t('gateways.detail.registeredSensors')}</span><p className="mt-1 text-gray-900">{sensors.length}</p></div>
          </div>
        )}

        {gateway && sensors.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <h2 className="font-semibold text-gray-900">{t('gateways.detail.noRegisteredSensors')}</h2>
            <p className="mt-2 text-sm text-gray-500">{t('gateways.detail.noRegisteredSensorsHint')}</p>
          </div>
        ) : gateway && (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-6 items-start">
            <aside className="bg-white border border-gray-200 rounded-lg overflow-hidden lg:sticky lg:top-4">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-sm font-semibold text-gray-900">{t('gateways.detail.registeredSensors')}</h2>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {filteredSensors.length}/{sensors.length}
                  </span>
                </div>
                <label htmlFor="gateway-sensor-search" className="sr-only">
                  {t('gateways.detail.sensorSearchLabel')}
                </label>
                <input
                  id="gateway-sensor-search"
                  type="search"
                  value={sensorSearch}
                  onChange={(event) => setSensorSearch(event.target.value)}
                  placeholder={t('gateways.detail.sensorSearchPlaceholder')}
                  className="mt-3 h-9 w-full border border-gray-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="max-h-96 lg:max-h-[calc(100vh-14rem)] overflow-y-auto divide-y divide-gray-100 overscroll-contain">
                {filteredSensors.length > 0 ? (
                  filteredSensors.map((sensor) => (
                    <button
                      key={sensor.id}
                      type="button"
                      onClick={() => setSelectedSensorId(sensor.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        sensor.id === selectedSensorId ? 'bg-primary-50 border-l-4 border-primary-600 pl-3' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="block text-sm font-medium text-gray-900 break-words">{sensor.name}</span>
                      <span className="block mt-1 text-xs text-gray-500 font-mono break-all">{sensor.device_id}</span>
                      <span className="block mt-1 text-xs text-gray-500">{sensor.sensor_type}</span>
                      <span className={`block mt-1 text-xs ${sensor.grower_id ? 'text-gray-600' : 'text-amber-700 italic'}`}>
                        {t('sensors.grower')}: {sensor.grower_id
                          ? (sensor.grower_name || t('common.unknown'))
                          : t('sensors.unassigned')}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gateways.detail.noSensorMatches')}
                  </p>
                )}
              </div>
            </aside>

            {selectedSensor && (
              <main className="min-w-0 space-y-5">
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900 break-words">{selectedSensor.name}</h2>
                      <p className="mt-1 text-sm text-gray-500">
                        {latestReading
                          ? t('gateways.detail.lastReceived', { date: new Date(latestReading.timestamp).toLocaleString() })
                          : t('dashboard.noReading')}
                      </p>
                    </div>
                    <Link to={`/sensors/${selectedSensor.id}`} className="text-sm text-primary-600 hover:text-primary-800">
                      {t('dashboard.viewDetails')}
                    </Link>
                  </div>

                  {visibleMetrics.length > 0 && (
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                      {visibleMetrics.map((metric) => {
                        const value = latestReading?.[metric.key] as number;
                        return (
                          <div key={String(metric.key)} className="bg-white px-3 py-3 min-w-0">
                            <p className="text-xs text-gray-500 break-words">{metric.label}</p>
                            <p className="mt-1 text-base font-semibold text-gray-900 break-words">{metric.format(value)}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {latestReading?.raw_data && (
                    <details className="mt-5 border-t border-gray-200 pt-4">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700">{t('gateways.detail.rawPayload')}</summary>
                      <pre className="mt-3 max-h-72 overflow-auto bg-gray-950 text-gray-100 rounded-md p-4 text-xs whitespace-pre-wrap break-all">
                        {JSON.stringify(latestReading.raw_data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>

                <ReadingsChart
                  sensorId={selectedSensor.id}
                  sensorType={selectedSensor.sensor_type}
                  sensor={selectedSensor}
                  latestReading={latestReading}
                  onNewReading={handleNewReading}
                />
              </main>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GatewayDetail;
